import {
  ChangeDetectionStrategy,
  Component,
  inject,
  signal,
} from '@angular/core';
import { GrantRequest } from '@bindoc-projects/bindoc-permissions-lib/data';
import { BdSharePermissionInfo } from '../../permissions/share-permission-info.model';
import { BdUiSingleSelectComponent, BdUiSingleSelectConfig } from '@bindoc-projects/ui-lib/selection/ui';
import { ButtonDirective } from 'primeng/button';
import { DynamicDialogConfig, DynamicDialogRef } from 'primeng/dynamicdialog';
import { of } from 'rxjs';
import { BdUiDialogPrimaryActionDirective } from '@bindoc-projects/ui-lib/dialog/ui';

export interface AcceptGrantRequestDialogData {
  grantRequest: GrantRequest;
  requesterName: string;
  shareableName: string;
}

export interface AcceptGrantRequestDialogResult {
  permission: BdSharePermissionInfo['permission'];
}

@Component({
  selector: 'bd-accept-grant-request-dialog',
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
    <div class="flex flex-col gap-3 p-4 min-w-[420px]">
      <p>
        <strong>{{ data.requesterName }}</strong> möchte Zugriff auf
        <strong>{{ data.shareableName }}</strong> erhalten.
      </p>
      @if (data.grantRequest.comment) {
        <div class="text-sm text-gray-600 italic">
          „{{ data.grantRequest.comment }}"
        </div>
      }
      <bd-ui-single-select
        [config]="selectConfig"
        [selectOptions]="permissionOptions$"
        [initialSelection]="initialPermission"
        (selection)="onSelect($event)"
      ></bd-ui-single-select>
      <div class="flex justify-end gap-2 pt-2">
        <button pButton text (click)="cancel()"><span>Abbrechen</span></button>
        <button
          pButton
          bdDialogPrimaryAction
          [disabled]="!selectedPermission()"
          (click)="submit()"
        >
          <span>Annehmen</span>
        </button>
      </div>
    </div>
  `,
  imports: [
    BdUiDialogPrimaryActionDirective,
    ButtonDirective,
    BdUiSingleSelectComponent,
  ],
})
export class BdAcceptGrantRequestDialogComponent {
  private readonly _ref = inject(DynamicDialogRef);
  private readonly _config =
    inject<DynamicDialogConfig<AcceptGrantRequestDialogData>>(
      DynamicDialogConfig,
    );

  public readonly data: AcceptGrantRequestDialogData = this._requireData();
  public readonly initialPermission = BdSharePermissionInfo.of('READ');
  public readonly selectedPermission = signal<
    BdSharePermissionInfo | undefined
  >(this.initialPermission);

  public readonly permissionOptions$ = of(BdSharePermissionInfo.tree('DELETE'));
  public readonly selectConfig: BdUiSingleSelectConfig<BdSharePermissionInfo> =
    {
      selectLabel: 'Berechtigung',
      option: {
        uniqueFnc: (i) => i.permission,
        sortFnc: (a, b) => a.weight - b.weight,
        searchOptionAccessor: (i) => i.explanation,
      },
    };

  public onSelect(info: BdSharePermissionInfo | undefined): void {
    this.selectedPermission.set(info);
  }

  public submit(): void {
    const sel = this.selectedPermission();
    if (!sel) {
      return;
    }
    this._ref.close({ permission: sel.permission });
  }

  public cancel(): void {
    this._ref.close(null);
  }

  private _requireData(): AcceptGrantRequestDialogData {
    if (!this._config.data) {
      throw new Error(
        'BdAcceptGrantRequestDialogComponent requires dialog data',
      );
    }
    return this._config.data;
  }
}
